// /app/frontend/src/hooks/useRoomMemory.js
// PATCH B: Room Memory - Tracks user's room participation history

import { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from "../api/client";
import { AuthContext } from '../App';

const API = window.location.origin;

/**
 * Hook to fetch user's room participation history
 * 
 * @returns {Object} { history, totalRooms, loading, error, refetch }
 */
export function useRoomHistory() {
  const { token } = useContext(AuthContext);
  const [history, setHistory] = useState([]);
  const [totalRooms, setTotalRooms] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await apiClient.get(`/me/rooms/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data.history || []);
      setTotalRooms(res.data.total_rooms || 0);
      setError(null);
    } catch (err) {
      console.error('[useRoomHistory] Error:', err);
      setError(err.response?.data?.detail || 'Failed to fetch room history');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return { history, totalRooms, loading, error, refetch: fetchHistory };
}

/**
 * Hook to fetch user's participation in a specific room
 * 
 * @param {string} roomKey - The room slug/key
 * @returns {Object} { participation, hasVisited, loading, error, refetch }
 */
export function useRoomParticipation(roomKey) {
  const { token } = useContext(AuthContext);
  const [participation, setParticipation] = useState(null);
  const [hasVisited, setHasVisited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchParticipation = useCallback(async () => {
    if (!token || !roomKey) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await apiClient.get(`/me/rooms/${roomKey}/participation`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setParticipation(res.data.participation);
      setHasVisited(res.data.has_visited || false);
      setError(null);
    } catch (err) {
      console.error('[useRoomParticipation] Error:', err);
      setError(err.response?.data?.detail || 'Failed to fetch room participation');
    } finally {
      setLoading(false);
    }
  }, [token, roomKey]);

  useEffect(() => {
    fetchParticipation();
  }, [fetchParticipation]);

  return { participation, hasVisited, loading, error, refetch: fetchParticipation };
}
